import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { zodResolver } from '@hookform/resolvers/zod';
import omit from 'lodash/omit';
import type { MutationStatus, UseMutationResult } from '@tanstack/react-query';
import { ProviderType, SmsCodeInputs, SmsCodeSchema } from '@repo/shared/types';
import { smsCodeInputFieldNames } from '@repo/shared/constants';
import { Button, SmsCode } from '@repo/ui';
import { maskPhoneNumber, joinArrayElements } from '@repo/shared/helpers';
import { useIsMobile } from '@repo/mui-config/hooks';
import type { MerchantResponse } from '@repo/api/endpoints/merchant';
import type {
  LoanSignVerifyRequestBody,
  LoanSignVerifySuccessResponseBody,
  LoanSignRequestBodySchema,
  LoanSignSuccessResponseBody,
} from '@repo/api/endpoints/loan';
import type { SmsId } from '@repo/shared/types';
import type { ResponseError } from '@repo/api/types';

import { AgreementSmsConfirmationCountdown } from '@/components/loan-process/loan-stages/stage-3/agreement-sms-confirmation/agreement-sms-confiramation-countdown';
import { useLoanProcessSetCurrentModalState } from '@/components/loan-process/hooks/use-loan-process-set-current-modal-state';
import type { LoanStoreData } from '@/components/loan-process/store/loan-process.store';
import { LoanProcessModalHeader } from '@/components/loan-process/loan-process-modal/loan-process-modal-header';

const MAX_RESEND_CODE_ATTEMPTS = 5;

const getLoanSignPayload = (
  merchantResponseData: MerchantResponse,
  loanData: LoanStoreData,
  selectedProvider: ProviderType,
) => {
  return {
    requestId: merchantResponseData.requestId,
    loanId: loanData.id,
    provider: selectedProvider,
    phoneNumber: merchantResponseData.data.merchantData.phone,
  };
};

const shouldSendRequestForFirstSmsCode = (loanSMSIds: SmsId[]) => {
  return loanSMSIds.length === 0;
};

const canResendRequestForSmsCode = (
  loanSMSIds: SmsId[],
  signStatus: MutationStatus,
  verifyStatus: MutationStatus,
) => {
  return (
    loanSMSIds.length < MAX_RESEND_CODE_ATTEMPTS &&
    signStatus !== 'pending' &&
    verifyStatus !== 'pending'
  );
};

export interface LoanSignMutation extends Pick<
  UseMutationResult<
    LoanSignSuccessResponseBody<'pragma'>,
    ResponseError,
    LoanSignRequestBodySchema
  >,
  'data' | 'status'
> {
  handleSignMutation: (data: LoanSignRequestBodySchema) => void;
}
export interface LoanVerifyMutation extends Pick<
  UseMutationResult<LoanSignVerifySuccessResponseBody, ResponseError, LoanSignVerifyRequestBody>,
  'status'
> {
  hasWrongSMSCode: boolean;
  handleSubmitForm: (data: LoanSignVerifyRequestBody) => void;
}

interface AgreementSmsConfirmation {
  merchantResponseData: MerchantResponse;
  loanData: LoanStoreData;
  loanSMSIds: SmsId[];
  loanSignMutation: LoanSignMutation;
  loanVerifyMutation: LoanVerifyMutation;
  handleCloseButton: () => void;
  selectedProvider: ProviderType;
}

export function AgreementSmsConfirmation({
  merchantResponseData,
  loanData,
  loanSMSIds,
  loanSignMutation,
  loanVerifyMutation,
  handleCloseButton,
  selectedProvider,
}: AgreementSmsConfirmation) {
  const { handleSignMutation, status: signStatus } = loanSignMutation;

  const { handleSubmitForm, hasWrongSMSCode, status: verifyStatus } = loanVerifyMutation;

  useLoanProcessSetCurrentModalState('AgreementSmsConfirmation');

  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const methods = useForm<SmsCodeInputs>({
    resolver: zodResolver(SmsCodeSchema),
    defaultValues: {
      smsCode: {
        firstCodeNumber: '',
        secondCodeNumber: '',
        thirdCodeNumber: '',
        forthCodeNumber: '',
        fifthCodeNumber: '',
        sixthCodeNumber: '',
        seventhCodeNumber: '',
      },
      finalSmsCode: 0,
    },
  });
  const isSmsCodeError = Boolean(methods.formState.errors.smsCode);

  const sendRequestForSMSCode = () => {
    handleSignMutation(getLoanSignPayload(merchantResponseData, loanData, selectedProvider));
  };

  const onSubmit: SubmitHandler<SmsCodeInputs> = (data) => {
    const payload = omit(
      getLoanSignPayload(merchantResponseData, loanData, selectedProvider),
      'phoneNumber',
    );
    handleSubmitForm({
      ...payload,
      method: 'sms',
      smsId: loanSMSIds[loanSMSIds.length - 1],
      smsCode: joinArrayElements(Object.values(data.smsCode)),
    });
  };

  useEffect(() => {
    if (shouldSendRequestForFirstSmsCode(loanSMSIds)) {
      sendRequestForSMSCode();
    }
  }, [loanSMSIds]);

  useEffect(() => {
    if (hasWrongSMSCode) {
      smsCodeInputFieldNames.forEach((value) => {
        methods.setError(`smsCode.${value}`, {
          message: t('loanProcess.loanSteps.sms.confirmation.form.validation.sms'),
        });
      });
    }
  }, [hasWrongSMSCode, methods, t]);

  return (
    <Box data-testid="agreement-sms-confirmation" sx={{ px: isMobile ? 2 : 4, py: 4 }}>
      <LoanProcessModalHeader
        closeButton={{ onClick: handleCloseButton }}
        containerSx={{ mb: 4 }}
      />
      <Stack alignItems="center" justifyItems="center" sx={{ marginBottom: '30px' }}>
        <Box>
          <Typography component="p" sx={{ marginBottom: 2 }} textAlign="center" variant="h5">
            {t('loanProcess.loanSteps.sms.confirmation.title')}
          </Typography>
          <Stack direction={{ sm: 'row' }} gap={1}>
            <Typography display={{ md: 'inline' }} textAlign="center">
              {t('loanProcess.loanSteps.sms.confirmation.description')}
            </Typography>
            <Typography align="center" component="span" variant="body2">
              {merchantResponseData.data.merchantData.phone
                ? maskPhoneNumber(merchantResponseData.data.merchantData.phone)
                : null}
            </Typography>
          </Stack>
        </Box>
      </Stack>
      <FormProvider {...methods}>
        <form
          data-testid="agreement-sms-confirmation-form"
          onSubmit={(event) => void methods.handleSubmit(onSubmit)(event)}
        >
          <Box sx={{ paddingX: { md: '8px', sm: '16px' } }}>
            <Typography
              data-testid="sms-code-counter"
              sx={{ marginBottom: '12px' }}
              variant="body2"
            >
              {t('loanProcess.loanSteps.sms.confirmation.enterSmsCode', {
                number: loanSMSIds.length,
              })}
            </Typography>
            <SmsCode isSmsCodeError={isSmsCodeError} />
            <AgreementSmsConfirmationCountdown
              isEnabled={canResendRequestForSmsCode(loanSMSIds, signStatus, verifyStatus)}
              onCountdownClick={sendRequestForSMSCode}
            />
          </Box>
          <Button
            color="secondary"
            data-testid="agreement-sms-confirmation-submit-button"
            disabled={Boolean(
              isSmsCodeError || signStatus === 'pending' || verifyStatus === 'pending',
            )}
            fullWidth
            size="large"
            sx={{ marginTop: '48px', paddingY: '14px' }}
            type="submit"
            variant="contained"
          >
            {t('loanProcess.loanSteps.sms.confirmation.button')}
          </Button>
        </form>
      </FormProvider>
    </Box>
  );
}
